import type { SupabaseClient } from '../supabase/client';
import type { Json } from '../supabase/database.types';
import type {
  Product,
  ProductImage,
  ProductImageWithUrl,
  ProductWithImages,
  ProductWithVariants,
  ProductVariant,
  ProductVariantOption,
  ProductVariantOptionValue,
  ProductVariantWithOptions,
  CreateProductInput,
  CreateProductWithVariantsInput,
  UpdateProductInput,
  UpdateProductWithVariantsInput,
  ProductErrorKey,
} from '../types/product';
import type { ProductCategorySummary } from '../types/category';
import { getFileExtension } from '../lib/image-validation';
import { mapBarcodeProductError } from './barcode.service';

const PRODUCT_IMAGES_BUCKET = 'product-images';
const PRODUCT_LIST_LIMIT = 50;

type ProductRowWithRelations = Product & {
  images: ProductImage[] | null;
  category: { id: string; name: string; media?: { id: string }[] | { id: string } | null } | null;
};

function hydrateCategorySummary(
  category: ProductRowWithRelations['category'],
): ProductCategorySummary {
  return {
    id: category?.id ?? '',
    name: category?.name ?? '',
    image_url: null,
  };
}

/**
 * Resolve the public URL for a product image from its canonical
 * storage_path. Bucket is public; `getPublicUrl` is a pure string
 * builder (no network call), so this is cheap.
 */
function resolvePublicUrl(client: SupabaseClient, storagePath: string): string {
  const { data } = client.storage.from(PRODUCT_IMAGES_BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
}

/**
 * Attach resolved public URLs and sort by `order_index`. Supabase's
 * nested-select ordering is unreliable, so we sort client-side.
 */
function hydrateImages(
  client: SupabaseClient,
  images: ProductImage[] | null | undefined,
): ProductImageWithUrl[] {
  if (!images || images.length === 0) return [];
  return [...images]
    .sort((a, b) => a.order_index - b.order_index)
    .map((img) => ({ ...img, url: resolvePublicUrl(client, img.storage_path) }));
}

function computeVariantAvailabilityState(
  variant: Pick<ProductVariant, 'active' | 'inventory'>,
): ProductVariantWithOptions['availability_state'] {
  if (!variant.active) return 'disabled';
  if (variant.inventory <= 0) return 'out_of_stock';
  if (variant.inventory <= 5) return 'low_stock';
  return 'available';
}

function buildVariantInputPayload(
  input: CreateProductWithVariantsInput | UpdateProductWithVariantsInput,
): {
  readonly product: Json;
  readonly mode: string;
  readonly options: Json;
  readonly variants: Json;
} {
  return {
    product: input.product as unknown as Json,
    mode: input.mode,
    options: input.options.map((option) => ({
      id: option.id,
      name: option.name,
      display_order: option.display_order,
      values: option.values.map((value) => ({
        id: value.id,
        value: value.value,
        display_order: value.display_order,
      })),
    })) as unknown as Json,
    variants: input.variants.map((variant) => ({
      id: variant.id,
      price_cents: variant.price_cents,
      sale_price_cents: variant.sale_price_cents ?? null,
      currency: variant.currency,
      inventory: variant.inventory,
      active: variant.active,
      is_default: variant.is_default ?? false,
      image_id: variant.image_id ?? null,
      display_order: variant.display_order,
      option_value_ids: [...variant.option_value_ids],
      barcode_value: variant.barcode_value ?? null,
      previous_barcode_normalized: variant.previous_barcode_normalized ?? null,
      barcode_replace_confirmed: variant.barcode_replace_confirmed ?? false,
      barcode_clear_confirmed: variant.barcode_clear_confirmed ?? false,
      barcode_deleted_reuse_confirmed_normalized:
        variant.barcode_deleted_reuse_confirmed_normalized ?? null,
    })) as unknown as Json,
  };
}

async function hydrateProductWithVariants(
  client: SupabaseClient,
  product: ProductWithImages,
): Promise<ProductWithVariants> {
  const [{ data: variantRows, error: variantsError }, { data: optionRows, error: optionsError }] =
    await Promise.all([
      client
        .from('product_variants')
        .select('*')
        .eq('product_id', product.id)
        .is('deleted_at', null)
        .order('display_order', { ascending: true })
        .order('id', { ascending: true }),
      client
        .from('product_variant_options')
        .select('*')
        .eq('product_id', product.id)
        .is('deleted_at', null)
        .order('display_order', { ascending: true })
        .order('id', { ascending: true }),
    ]);

  if (variantsError) throw variantsError;
  if (optionsError) throw optionsError;

  const variants = (variantRows ?? []) as unknown as ProductVariant[];
  const options = (optionRows ?? []) as unknown as ProductVariantOption[];
  const optionIds = options.map((option) => option.id);

  let optionValues: ProductVariantOptionValue[] = [];
  if (optionIds.length > 0) {
    const { data, error } = await client
      .from('product_variant_option_values')
      .select('*')
      .in('option_id', optionIds)
      .is('deleted_at', null)
      .order('display_order', { ascending: true })
      .order('id', { ascending: true });
    if (error) throw error;
    optionValues = (data ?? []) as unknown as ProductVariantOptionValue[];
  }

  const variantIds = variants.map((variant) => variant.id);
  const assignmentByVariant = new Map<string, ProductVariantWithOptions['values']>();
  if (variantIds.length > 0) {
    const { data, error } = await client
      .from('product_variant_value_assignments')
      .select('variant_id, option_id, option_value_id')
      .in('variant_id', variantIds);
    if (error) throw error;

    for (const assignment of (data ?? []) as ReadonlyArray<{
      variant_id: string;
      option_id: string;
      option_value_id: string;
    }>) {
      const option = options.find((candidate) => candidate.id === assignment.option_id);
      const value = optionValues.find((candidate) => candidate.id === assignment.option_value_id);
      if (!option || !value) continue;

      const existing = assignmentByVariant.get(assignment.variant_id) ?? [];
      assignmentByVariant.set(assignment.variant_id, [
        ...existing,
        {
          option_id: option.id,
          option_name: option.name,
          value_id: value.id,
          value: value.value,
          display_order: value.display_order,
        },
      ]);
    }
  }

  const imageById = new Map(product.images.map((image) => [image.id, image]));
  const hydratedVariants: ProductVariantWithOptions[] = variants.map((variant) => {
    const values = [...(assignmentByVariant.get(variant.id) ?? [])].sort(
      (a, b) => a.display_order - b.display_order || a.value_id.localeCompare(b.value_id),
    );
    return {
      ...variant,
      values,
      image: variant.image_id ? (imageById.get(variant.image_id) ?? null) : null,
      option_summary: values.map((value) => `${value.option_name}: ${value.value}`).join(' / '),
      availability_state: computeVariantAvailabilityState(variant),
    };
  });

  const activeVariants = hydratedVariants.filter((variant) => variant.active);
  const summary = [...activeVariants].sort((a, b) => {
    const aEffective = a.sale_price_cents ?? a.price_cents;
    const bEffective = b.sale_price_cents ?? b.price_cents;
    return (
      aEffective - bEffective ||
      a.price_cents - b.price_cents ||
      a.display_order - b.display_order ||
      a.id.localeCompare(b.id)
    );
  })[0];
  const saleSummary = [...activeVariants]
    .filter((variant) => variant.sale_price_cents !== null)
    .sort((a, b) => {
      const saleDelta = Number(a.sale_price_cents) - Number(b.sale_price_cents);
      return (
        saleDelta ||
        a.price_cents - b.price_cents ||
        a.display_order - b.display_order ||
        a.id.localeCompare(b.id)
      );
    })[0];

  const effectivePriceSet = new Set(
    activeVariants.map((variant) => variant.sale_price_cents ?? variant.price_cents),
  );

  return {
    ...product,
    mode: options.length > 0 ? 'options' : 'simple',
    variants: hydratedVariants,
    options,
    optionValues,
    variant_count: hydratedVariants.length,
    active_variant_count: activeVariants.length,
    total_active_inventory: activeVariants.reduce((sum, variant) => sum + variant.inventory, 0),
    summary_variant_id: summary?.id ?? null,
    summary_regular_price_cents: summary?.price_cents ?? product.price_cents,
    summary_sale_price_cents: summary?.sale_price_cents ?? null,
    summary_effective_price_cents: summary
      ? (summary.sale_price_cents ?? summary.price_cents)
      : product.price_cents,
    sale_summary_variant_id: saleSummary?.id ?? null,
    sale_summary_regular_price_cents: saleSummary?.price_cents ?? null,
    sale_summary_sale_price_cents: saleSummary?.sale_price_cents ?? null,
    sale_summary_effective_price_cents: saleSummary?.sale_price_cents ?? null,
    has_variant_sale: saleSummary !== undefined,
    from_price: effectivePriceSet.size > 1,
    has_disabled_variants: hydratedVariants.some((variant) => !variant.active),
    has_out_of_stock_variants: hydratedVariants.some(
      (variant) => variant.active && variant.inventory <= 0,
    ),
  };
}

/**
 * List live products for a store, newest first, capped at
 * PRODUCT_LIST_LIMIT. Resolves each image's public URL and returns
 * ProductWithImages. Throws on Supabase error.
 */
export async function listProducts(
  client: SupabaseClient,
  storeId: string,
): Promise<ProductWithVariants[]> {
  const { data, error } = await client
    .from('products')
    .select(
      '*, images:product_images(*), category:store_categories(id, name, media:category_media(id))',
    )
    .eq('store_id', storeId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(PRODUCT_LIST_LIMIT);

  if (error) throw error;
  const rows = (data ?? []) as unknown as ProductRowWithRelations[];

  const products = rows.map((row) => {
    const { images, category, ...product } = row;
    return {
      ...(product as Product),
      images: hydrateImages(client, images),
      category: hydrateCategorySummary(category),
    };
  });

  return Promise.all(products.map((product) => hydrateProductWithVariants(client, product)));
}

/**
 * Fetch one product with its images. Returns null when not found so
 * the caller can map to the notFound error key.
 */
export async function getProduct(
  client: SupabaseClient,
  productId: string,
): Promise<ProductWithVariants | null> {
  const { data, error } = await client
    .from('products')
    .select(
      '*, images:product_images(*), category:store_categories(id, name, media:category_media(id))',
    )
    .eq('id', productId)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const row = data as unknown as ProductRowWithRelations;
  const { images, category, ...productRow } = row;
  const hydratedProduct = {
    ...(productRow as Product),
    images: hydrateImages(client, images),
    category: hydrateCategorySummary(category),
  };
  return hydrateProductWithVariants(client, hydratedProduct);
}

/**
 * Create a product row. Does NOT upload images — caller must chain
 * uploadAndAttachProductImage() per file.
 */
export async function createProduct(
  client: SupabaseClient,
  input: CreateProductInput,
): Promise<Product> {
  return createProductWithVariants(client, {
    product: {
      store_id: input.store_id,
      name: input.name.trim(),
      description: input.description?.trim() || null,
      currency: input.currency,
      category_id: input.category_id,
    },
    mode: 'simple',
    options: [],
    variants: [
      {
        price_cents: input.price_cents,
        sale_price_cents: input.sale_price_cents ?? null,
        currency: input.currency,
        inventory: input.inventory,
        active: true,
        is_default: true,
        display_order: 0,
        option_value_ids: [],
      },
    ],
  });
}

export async function createProductWithVariants(
  client: SupabaseClient,
  input: CreateProductWithVariantsInput,
): Promise<Product> {
  const payload = buildVariantInputPayload(input);
  const { data, error } = await client.rpc('create_product_with_variants', {
    product_payload: payload.product,
    mode: payload.mode,
    options_payload: payload.options,
    variants_payload: payload.variants,
  });
  if (error) throw error;
  return data as Product;
}

/**
 * Partial update of product columns. The DB column grant limits which
 * columns can actually be updated (name/description/price_cents/currency/inventory).
 */
export async function updateProduct(
  client: SupabaseClient,
  productId: string,
  input: UpdateProductInput,
): Promise<Product> {
  const current = await getProduct(client, productId);
  if (!current) {
    throw Object.assign(new Error('product not found'), { code: 'P0002' });
  }

  const productPatch: UpdateProductInput = { ...input };
  if (input.name !== undefined) productPatch.name = input.name.trim();
  if (input.description !== undefined) productPatch.description = input.description?.trim() || null;

  return updateProductWithVariants(client, productId, {
    product: productPatch,
    mode: current.mode,
    options: current.options.map((option) => ({
      id: option.id,
      name: option.name,
      display_order: option.display_order,
      values: current.optionValues
        .filter((value) => value.option_id === option.id)
        .map((value) => ({
          id: value.id,
          value: value.value,
          display_order: value.display_order,
        })),
    })),
    variants: current.variants.map((variant) => ({
      id: variant.id,
      price_cents: productPatch.price_cents ?? variant.price_cents,
      sale_price_cents:
        productPatch.sale_price_cents !== undefined
          ? productPatch.sale_price_cents
          : variant.sale_price_cents,
      currency: variant.currency,
      inventory: productPatch.inventory ?? variant.inventory,
      active: variant.active,
      is_default: variant.is_default,
      image_id: variant.image_id,
      display_order: variant.display_order,
      option_value_ids: variant.values.map((value) => value.value_id),
      barcode_value: variant.barcode_value,
      previous_barcode_normalized: variant.barcode_normalized,
    })),
  });
}

export async function updateProductWithVariants(
  client: SupabaseClient,
  productId: string,
  input: UpdateProductWithVariantsInput,
): Promise<Product> {
  const payload = buildVariantInputPayload(input);
  const { data, error } = await client.rpc('update_product_with_variants', {
    target_product_id: productId,
    product_payload: payload.product,
    mode: payload.mode,
    options_payload: payload.options,
    variants_payload: payload.variants,
  });
  if (error) throw error;
  return data as Product;
}

/**
 * Cascading soft delete via RPC. The RPC deletes storage objects and
 * product_images rows, then sets deleted_at on the product. The caller
 * doesn't need to clean anything up — the DB handles all of it inside
 * a SECURITY DEFINER transaction.
 */
export async function softDeleteProduct(client: SupabaseClient, productId: string): Promise<void> {
  const { error } = await client.rpc('soft_delete_product', {
    target_product_id: productId,
  });
  if (error) throw error;
}

/**
 * Upload a single image to product-images bucket and return both the
 * storage path and the resolved public URL. Does NOT insert a
 * product_images row — see uploadAndAttachProductImage for the full
 * transactional flow.
 *
 * Path convention: {storeId}/{crypto.randomUUID()}.{ext}
 * (No product_id segment — C1/D2 makes the DB row the authoritative
 *  link between image and product.)
 *
 * Hardening (W1):
 * - contentType is set to the validated File.type
 * - cacheControl is set to '31536000' (1 year)
 * - upsert is false (never overwrite)
 */
export async function uploadProductImage(
  client: SupabaseClient,
  args: { storeId: string; file: File },
): Promise<{ storagePath: string; publicUrl: string }> {
  const { storeId, file } = args;
  // File.type is the validated MIME — caller must run validateProductImageFile()
  // before calling. We extract the extension from the MIME, not the
  // filename, so a spoofed `.jpg` with `image/png` MIME gets the correct
  // `.png` extension (and the DB path CHECK still accepts it).
  const ext = getFileExtension(file.type);
  const nonce = crypto.randomUUID();
  const storagePath = `${storeId}/${nonce}.${ext}`;

  const { error } = await client.storage.from(PRODUCT_IMAGES_BUCKET).upload(storagePath, file, {
    contentType: file.type,
    cacheControl: '31536000',
    upsert: false,
  });

  if (error) throw error;

  const publicUrl = resolvePublicUrl(client, storagePath);
  return { storagePath, publicUrl };
}

/**
 * The full transactional upload-then-attach flow for ONE image (D3).
 *   1. upload the file to the bucket
 *   2. insert the matching product_images row
 *   3. on step 2 failure: best-effort DELETE the just-uploaded object
 * Throws on any failure; the caller is responsible for tracking which
 * files succeeded and surfacing a partial-failure warning.
 */
export async function uploadAndAttachProductImage(
  client: SupabaseClient,
  args: {
    storeId: string;
    productId: string;
    orderIndex: number;
    file: File;
  },
): Promise<ProductImageWithUrl> {
  const { storeId, productId, orderIndex, file } = args;

  // Step 1: upload the binary
  const { storagePath, publicUrl } = await uploadProductImage(client, { storeId, file });

  // Step 2: insert the DB row. On failure, best-effort-delete the
  // object so we don't orphan storage.
  const { data, error } = await client
    .from('product_images')
    .insert({
      product_id: productId,
      storage_path: storagePath,
      order_index: orderIndex,
      // store_id_cache is populated by the BEFORE INSERT trigger; we
      // must provide SOME value because of NOT NULL + the generated
      // TypeScript Insert type. The trigger overrides it to the
      // authoritative value from products.store_id, so passing storeId
      // here is safe and aligns with the types.
      store_id_cache: storeId,
    })
    .select()
    .single();

  if (error) {
    // Compensating delete. Uses the best-effort helper so a double
    // failure still surfaces the ORIGINAL insert error to the caller.
    await deleteProductImageObject(client, storagePath);
    throw error;
  }

  // Cast through unknown: Supabase's generated Row type may lag behind
  // the migration (file_size_bytes is trigger-populated). The SELECT *
  // returns the full row including trigger-written columns.
  const image = data as unknown as ProductImage;
  return { ...image, url: publicUrl };
}

/**
 * Delete a single image from both the DB and storage. Used when the
 * user removes an image in the edit form.
 *
 * Calls the `delete_product_image` SECURITY DEFINER RPC which deletes
 * the `storage.objects` row and the `product_images` row in a single
 * transaction — no orphan risk.  See INFRA-TBD-orphan-sweep design doc.
 */
export async function deleteProductImage(
  client: SupabaseClient,
  image: ProductImage,
): Promise<void> {
  const { error } = await client.rpc('delete_product_image', {
    target_image_id: image.id,
  });
  if (error) throw error;
}

/**
 * Reorder a product's images. Wraps the reorder_product_images RPC,
 * which does the two-phase update inside a single transaction to work
 * around the UNIQUE (product_id, order_index) constraint (D1).
 * orderedImageIds must be a complete permutation of the product's
 * current image IDs — partial lists raise.
 */
export async function reorderProductImages(
  client: SupabaseClient,
  args: { productId: string; orderedImageIds: string[] },
): Promise<void> {
  const { error } = await client.rpc('reorder_product_images', {
    target_product_id: args.productId,
    ordered_image_ids: args.orderedImageIds,
  });
  if (error) throw error;
}

/**
 * Best-effort cleanup when an upload succeeded but the DB row insert
 * failed (or when the caller wants to drop a storage object orphaned
 * by a higher-level failure). Mirrors deleteStoreLogo's catch-and-log
 * shape — never throws, always logs on failure.
 */
export async function deleteProductImageObject(
  client: SupabaseClient,
  storagePath: string,
): Promise<void> {
  try {
    const { error } = await client.storage.from(PRODUCT_IMAGES_BUCKET).remove([storagePath]);
    if (error) {
      console.error('[productService.deleteProductImageObject] best-effort remove failed:', error);
    }
  } catch (err) {
    console.error('[productService.deleteProductImageObject] unexpected:', err);
  }
}

/**
 * Whitelist error → translation key mapper. Matches the shape of
 * F-002's `mapStoreError`: inspect the error, return a fixed key, and
 * degrade to `unknown` for anything unrecognized.
 *
 * The full mapping table lives in design §5.
 */
export function mapProductError(err: unknown): ProductErrorKey {
  if (!err || typeof err !== 'object') return 'common.errors.unknown';

  // Network failures surface as plain fetch TypeError.
  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'common.errors.networkFailed';
  }

  const maybe = err as {
    code?: string;
    message?: string;
    name?: string;
    status?: number;
    statusCode?: string | number;
    constraint?: string;
    details?: string;
  };

  const barcodeKey = mapBarcodeProductError(err);
  if (barcodeKey !== 'common.errors.unknown') return barcodeKey;

  if (maybe.name === 'AuthRetryableFetchError') {
    return 'common.errors.networkFailed';
  }

  // Postgres error codes
  switch (maybe.code) {
    case '23503': // FK violation — product_id of a deleted product, etc.
      return 'admin.products.errors.notFound';
    case '23514': {
      // CHECK violation — distinguish bidi-override from everything else.
      const fingerprint = [maybe.constraint ?? '', maybe.message ?? '', maybe.details ?? ''].join(
        ' ',
      );
      if (/products_name_no_bidi_override_chk|name.*bidi/i.test(fingerprint)) {
        return 'admin.products.errors.nameInvalid';
      }
      if (/products_description_no_bidi_override_chk|description.*bidi/i.test(fingerprint)) {
        return 'admin.products.errors.descriptionInvalid';
      }
      if (/products_sale_price_cents_valid_chk|sale_price_cents/i.test(fingerprint)) {
        return 'admin.products.errors.salePriceInvalid';
      }
      // C4 trigger raises with this text + check_violation code.
      if (/maximum 5 images per product/i.test(fingerprint)) {
        return 'admin.products.errors.tooManyImages';
      }
      // F-004-storage-quota: quota enforcement trigger raises with this
      // text + check_violation code when a store exceeds 50 MiB.
      if (/storage quota exceeded/i.test(fingerprint)) {
        return 'admin.products.errors.storageQuotaExceeded';
      }
      if (
        /PRODUCT_HAS_ACTIVE_ORDERS|VARIANT_HAS_ACTIVE_ORDERS|ordered order item/i.test(fingerprint)
      ) {
        return 'admin.products.errors.activeOrderedOrders';
      }
      return 'common.errors.unknown';
    }
    case '42501':
      return 'common.errors.permissionDenied';
    case 'P0002':
      return 'admin.products.errors.notFound';
    case 'P0006':
      return 'admin.products.errors.categoryRequired';
    case 'P0601':
      return 'admin.entitlements.upgradePrompt.description';
    case 'P0602':
      return 'admin.entitlements.storageLimitBlocked';
    case 'P0604':
      return 'admin.entitlements.aiQuotaBlocked';
    case 'P0606':
      return 'admin.entitlements.productLimitBlocked';
  }

  // RPC text-based raises (no SQLSTATE on some Supabase paths)
  const msg = maybe.message ?? '';
  if (/permission denied/i.test(msg)) return 'common.errors.permissionDenied';
  if (/not authenticated/i.test(msg)) return 'common.errors.permissionDenied';
  if (/product not found/i.test(msg)) return 'admin.products.errors.notFound';
  if (/PRODUCT_HAS_ACTIVE_ORDERS|VARIANT_HAS_ACTIVE_ORDERS|ordered order item/i.test(msg)) {
    return 'admin.products.errors.activeOrderedOrders';
  }
  if (/category/i.test(msg) && /required|not found|live/i.test(msg)) {
    return 'admin.products.errors.categoryRequired';
  }
  if (/maximum 5 images per product/i.test(msg)) return 'admin.products.errors.tooManyImages';
  if (/billing active product limit/i.test(msg)) return 'admin.entitlements.productLimitBlocked';
  if (/billing storage limit/i.test(msg)) return 'admin.entitlements.storageLimitBlocked';
  if (/billing ai quota/i.test(msg)) return 'admin.entitlements.aiQuotaBlocked';
  if (/billing entitlement required/i.test(msg)) {
    return 'admin.entitlements.upgradePrompt.description';
  }
  if (/ordered_image_ids must be a complete permutation/i.test(msg)) {
    return 'common.errors.unknown';
  }

  // Storage errors
  const statusCode = String(maybe.statusCode ?? '');
  if (statusCode === '413' || /payload too large/i.test(msg)) {
    return 'admin.products.errors.imageTooLarge';
  }
  if (/mime type/i.test(msg) || /not allowed/i.test(msg)) {
    return 'admin.products.errors.imageInvalidType';
  }
  if (/upload/i.test(msg) && /fail/i.test(msg)) {
    return 'admin.products.errors.imageUploadFailed';
  }

  return 'common.errors.unknown';
}
